import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSyncFlight } from '@/lib/api/queries';
import type { Flight } from '@/lib/api/types';
import { config } from '@/lib/config';
import { Clock } from 'lucide-react';

/** Live has NO DroneLogBook sync endpoint — nothing in the Custodian OpenAPI
 *  spec accepts a push; the import runs backend-side, which is why records
 *  arrive with `imported_at` already set. Reporting success there would be
 *  fabricated progress over real data, so live gets the same pending-backend
 *  treatment as the Inventory SyncDialog. */
export function SyncFlightDialog({
  flight,
  open,
  onOpenChange,
}: {
  flight: Flight;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const sync = useSyncFlight();

  if (!config.isMock) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync flight to DroneLogBook</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2.5 rounded-md border border-(--color-amber)/40 bg-(--color-amber-bg) p-3">
            <Clock size={16} className="text-(--color-amber) shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="text-[0.846em] font-bold text-(--color-amber)">
                Pending backend support
              </div>
              <p className="text-[0.769em] text-(--color-text2) leading-relaxed">
                The DroneLogBook push runs backend-side and the Custodian API exposes no endpoint to
                trigger it. Wire this up once BE adds one.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sync flight to DroneLogBook</DialogTitle>
        </DialogHeader>
        <p className="text-[0.769em] text-(--color-text2)">
          Push <span className="font-mono">{flight.flight_id}</span> to DroneLogBook.
        </p>
        {sync.isError && (
          <p className="text-[0.769em] text-(--color-red)">Sync failed. Try again.</p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={sync.isPending}
            onClick={() =>
              sync.mutate({ flightId: flight.flight_id }, { onSuccess: () => onOpenChange(false) })
            }
          >
            {sync.isPending ? 'Syncing…' : 'Sync Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
